import clsx from 'clsx';
import { Button } from '../../../button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../dialog';
import { useState } from 'react';
import LoadingSpinner from '../../../loading-spinner';
import { Input } from '../../../input';
import { cloneWorkspace, createNewWorkspace } from '../../../../../lib/api';
import { supabase } from '../../../../../lib/supabase';
import { NewWorkspaceAction, NewWorkspaceProps } from './types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../select';

const WORKSPACE_NAME_MAX_LENGTH = 100;

export function NewWorkspace({
  authUser,
  fetchWorkspaceProfiles,
}: NewWorkspaceProps) {
  const [workspaceName, setWorkspaceName] = useState('');
  const [actionType, setActionType] = useState<NewWorkspaceAction>('create');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = () => {
    if (actionType === 'create') return handleCreate();
    if (actionType === 'duplicate') return handleDuplicate();
  };

  const handleCreate = async () => {
    setError(null);
    setSuccess(null);

    if (
      workspaceName?.trim().length === 0 ||
      !workspaceName ||
      typeof workspaceName !== 'string'
    ) {
      setError('Workspace name cannot be empty.');
      return;
    }

    if (workspaceName?.length > WORKSPACE_NAME_MAX_LENGTH) {
      setError(
        `Workspace name cannot exceed ${WORKSPACE_NAME_MAX_LENGTH} characters.`,
      );
    }

    setIsLoading(true);

    try {
      await createNewWorkspace({ name: workspaceName?.trim() });

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfiles(session?.user);
        }
      });

      setSuccess(`Workspace "${workspaceName}" created successfully.`);
      setWorkspaceName('');
    } catch (err) {
      console.error('Error creating a workspace:', err);
      setError('Failed to create a new workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicate = async () => {
    setError(null);
    setSuccess(null);

    if (!workspaceName.trim()) {
      setError('Workspace name cannot be empty.');
      return;
    }

    setIsLoading(true);

    try {
      await cloneWorkspace({
        name: workspaceName?.trim(),
        workspaceId: authUser?.currentWorkspace?.workspace_id,
      });

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfiles(session?.user);
        }
      });

      setSuccess(`Workspace duplicated successfully.`);
      setWorkspaceName('');
    } catch (err) {
      console.error(err);
      setError('Failed to duplicate workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white">
          New workspace
        </Button>
      </DialogTrigger>

      <DialogContent className="flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>Create or duplicate a workspace</DialogDescription>
        </DialogHeader>

        {/* Create or duplicate dropdown */}
        <div className="mt-2 flex flex-col gap-1">
          <p className="text-sm">Select action type:</p>
          <Select
            value={actionType}
            onValueChange={(value: NewWorkspaceAction) => setActionType(value)}
          >
            <SelectTrigger className="flex-1 bg-prompt-safecomwhite font-prompt-safe-com-semantic-options text-prompt-safecomabbey">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="duplicate">Duplicate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-3 flex flex-col gap-1">
          <p className="text-sm">Workspace name:</p>
          <Input
            placeholder={
              actionType === 'create'
                ? 'My New Workspace'
                : 'My Duplicated Workspace'
            }
            value={workspaceName}
            onChange={(e) => {
              setWorkspaceName(e.target.value);
              if (error) setError(null);
              if (success) setSuccess(null);
            }}
            disabled={isLoading}
            maxLength={WORKSPACE_NAME_MAX_LENGTH}
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}
        </div>

        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className={clsx(
              'hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? (
              <LoadingSpinner strokeWidth={7} />
            ) : actionType === 'create' ? (
              'Create'
            ) : (
              'Duplicate'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
